import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ludsElevation } from '../../styles/elevation';
import { overrideDesignTokens } from '../../styles/override-design-tokens';
import resetStyle from '../html-content/reset.style';
import { ludsSpacing } from '../../styles/spacing';

/**
 *
 */
@customElement('lua-content-layout')
export class LuaContentLayout extends LitElement {
  @property({ type: String })
  variant: 'default' | 'sidebar-only' = 'default';

  @property({ type: Boolean })
  open = true;

  @property({ type: Boolean })
  isSidebarDisabled = false;

  @property({ type: Boolean })
  hideFooter = false;

  static override styles = [
    resetStyle,
    overrideDesignTokens,
    ludsElevation,
    ludsSpacing,
    css`
      :host {
        --sidenav-width: var(--spacing-spacing-48);
        --footer-height: var(--spacing-spacing-64);
        --header-height: var(--content-layout-header-height, 52px);
        --flash-message-height: var(--override-flash-message-height, 0px);
      }

      @keyframes shadow {
        0% {
          box-shadow: var(--luds-elevation-1);
        }
      }

      :host main.default,
      :host main.sidebar-only {
        display: grid;
        grid-template-columns: var(--sidenav-width) repeat(11, 1fr);
        grid-template-rows: auto 1fr auto;
        gap: 0;
        margin: 0;
        padding: 0;
        height: calc(100vh - var(--flash-message-height, 0px));

        &.open {
          --sidenav-width: 320px;
        }

        header {
          grid-area: 1 / 2 / 2 / 13;
          padding: 0;
          box-shadow: 0px 0px;
          animation: shadow linear calc(var(--scroll) * 1s) paused;
          z-index: 1;
          height: fit-content;
        }

        aside {
          grid-area: 1 / 1 / 4 / 1;
          padding: 0;
        }

        article {
          grid-area: 2 / 2 / 3 / 13;
          padding: 0;
          overflow-y: auto;
          overflow-x: hidden;

          border: 0;
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          container-type: size;
          height: auto;
        }

        .content {
          grid-column: 3 / 11;

          @media (max-width: 1220px) {
            grid-column: 2 / 12;
          }
        }

        footer {
          grid-area: 3 / 2 / 4 / 13;
          padding: 0;
          min-height: var(--footer-height);
        }
      }

      :host main.sidebar-only {
        header,
        footer {
          display: none;
        }
        article {
          grid-area: 1 / 2 / 4 / 13;
          align-items: center;
        }
      }

      :host main.main-only {
        grid-template-columns: max-content;
        grid-template-rows: auto 1fr;

        &.open {
          --sidenav-width: 0px;
        }

        .content {
          grid-column: 1 / 13;
        }

        article {
          max-height: calc(100vh - var(--header-height));
        }

        aside {
          grid-area: none;
          display: none;
        }
      }

      @media (max-width: 768px) {
        :host main.default {
          grid-template-columns: 1fr;

          header {
            grid-area: 1 / 1 / 1 / 13;
            position: sticky;
            top: 0;
          }

          footer {
            grid-area: 3 / 1 / 3 / 13;
            position: sticky;
            bottom: 0;
          }

          aside {
            grid: none;
          }

          article {
            grid-area: 2 / 1 / 2 / 13;
          }

          .margin-bottom-64 {
            margin-bottom: 64px;
          }
        }
      }
    `,
  ];

  @query('article')
  private article: HTMLElement;

  override firstUpdated() {
    if (this.article) {
      this.article.addEventListener('scroll', this.handleScroll.bind(this));
    }
  }

  override disconnectedCallback(): void {
    if (this.article) {
      this.article.removeEventListener('scroll', this.handleScroll.bind(this));
    }
    super.disconnectedCallback();
  }

  private handleScroll(event: Event) {
    // Set a --scroll css property
    const article = event.target as HTMLElement;
    const scrollOffset = article.scrollTop;

    // Get the maximum possible value of scrollTop
    const maxScrollTop = article.scrollHeight - article.clientHeight;

    this.style.setProperty('--scroll', `${scrollOffset}`);
    this.style.setProperty('--max-scroll-top', `${maxScrollTop}`);
  }

  protected override render() {
    return html`<main
      class=${classMap({
        open: this.open,
        [this.variant]: true,
        'main-only': this.isSidebarDisabled,
      })}>
      <aside>
        <slot name="sidenav"></slot>
      </aside>
      <header>
        <slot name="header"></slot>
      </header>
      <article
        class=${classMap({
          'margin-bottom-64': !this.hideFooter,
        })}>
        <div class="content">
          <slot name="content"></slot>
        </div>
      </article>
      ${this.hideFooter
        ? nothing
        : html`<footer>
            <slot name="footer"></slot>
          </footer>`}
    </main>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lua-content-layout': LuaContentLayout;
  }
}
